import {
  Component,
  OnInit,
  Renderer2,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy
} from '@angular/core';
import { PercentPipe } from '@angular/common';
import {
  FormGroup,
  UntypedFormGroup,
  FormArray,
  Validators,
  FormControl,
  AbstractControl
} from '@angular/forms';
import { forkJoin, Observable } from 'rxjs';
import { SubscriptionSink, Utilities, isTrueLike } from '@approot/utilities';
import {
  TradesCaptureService,
  LegService,
  AlertsService
} from '@approot/services';
import {
  TreasuryCurve,
  Entity,
  KeyValue,
  RequestStatus
} from '@approot/models';

type DateMode = 'TENOR' | 'DATE';

@Component({
  selector: 'app-treasury-curves',
  templateUrl: './treasury-curves.component.html',
  styleUrls: ['./treasury-curves.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TreasuryCurvesComponent implements OnInit, OnDestroy {
  public treasuryCurvesForm: FormGroup;
  public selectedCurveIndex: number = null;
  public selectedTenorIndex: number = null;
  public savingCurvesStatus: RequestStatus = '';
  public selectedPercentField = null;
  public loadingCurves = true;
  public identifiers: string[] = ['AXA'];
  public dateMode: DateMode = 'TENOR';

  @ViewChild('divToScrollCurves', { static: false })
  private divToScrollCurves: ElementRef;
  @ViewChild('divToScrollTenor', { static: false })
  private divToScrollTenor: ElementRef;

  private curvesToDelete = [];
  private subSink = new SubscriptionSink();
  private treasuryCurvesFormCurves: FormArray;

  constructor(
    private renderer: Renderer2,
    private tradesCaptureService: TradesCaptureService,
    private percentPipe: PercentPipe,
    private legService: LegService,
    private changeRef: ChangeDetectorRef,
    private alertsService: AlertsService
  ) {}

  public ngOnInit() {
    this.treasuryCurvesForm = new UntypedFormGroup({
      curves: new FormArray([])
    });

    this.treasuryCurvesFormCurves = this.treasuryCurvesForm.get(
      'curves'
    ) as FormArray;

    let requests: Record<string, Observable<unknown>> = {};

    requests['entities'] = this.tradesCaptureService.getEntities();

    requests['treasuryCurves'] = this.tradesCaptureService.getUserInfoRequest({
      task: 'treasuryCurve:read'
    });

    forkJoin(requests).subscribe({
      next: (response: {
        entities: Entity[];
        treasuryCurves: TreasuryCurve[];
      }) => {
        const entities = response.entities;
        const treasuryCurves = response.treasuryCurves;

        const dateTypes: DateMode[] = [
          ...new Set(
            treasuryCurves.map(curve => (curve.tenors ? 'TENOR' : 'DATE'))
          )
        ];
        if (dateTypes.length > 1) {
          this.alertsService.addAlert({
            message:
              'Treasury curves must be of the same type (tenor or date based)'
          });
          this.tradesCaptureService.setShowTreasuryCurves = false;
          return;
        } else if (dateTypes.length === 0) {
          dateTypes.push('TENOR');
        }
        this.dateMode = dateTypes[0];

        this.identifiers = entities
          .filter(entity => isTrueLike(entity.isMemberOfParentGroup))
          .map(entity => entity.identifier);
        this.updateLocallyStoredData(treasuryCurves, entities);

        treasuryCurves.forEach(curve => {
          if (!this.identifiers.includes(curve.entity)) {
            // if the entity not in the identifiers
            return;
          }

          this.treasuryCurvesFormCurves.push(
            this.createTreasuryCurveForm(curve)
          );
        });
        this.loadingCurves = false;
        this.changeRef.markForCheck();
      }
    });
  }

  public ngOnDestroy() {
    this.subSink.unsubscribe();
  }

  public createTreasuryCurveForm(curve: TreasuryCurve) {
    let tenors: FormArray;
    let dates: FormArray;
    if (this.dateMode === 'DATE') {
      dates = new FormArray([], Validators.required);
      tenors = new FormArray([]);
    } else {
      tenors = new FormArray([], Validators.required);
      dates = new FormArray([]);
    }
    const bids = new FormArray([], Validators.required);
    const asks = new FormArray([], Validators.required);

    if (curve?.tenors && this.dateMode === 'TENOR') {
      curve.tenors.forEach(tenor => {
        tenors.push(this.tenorControlMaker(tenor));
      });
    }

    if (curve?.dates && this.dateMode === 'DATE') {
      curve.dates.forEach(date => {
        dates.push(this.dateControlMaker(date));
      });
    }

    if (curve?.askRates) {
      curve.askRates.forEach(ask => {
        ask = Number(ask).toFixed(6); // -> 4dp when %age
        asks.push(this.askBidControlMaker(ask));
      });
    }
    if (curve?.bidRates) {
      curve.bidRates.forEach(bid => {
        bid = Number(bid).toFixed(6);
        bids.push(this.askBidControlMaker(bid));
      });
    }
    const form = new UntypedFormGroup({
      currency: new FormControl(curve?.currency || '', Validators.required),
      entity: new FormControl(curve?.entity || '', [Validators.required]),
      dayCountConvention: new FormControl(
        curve?.dayCountConvention || 'Act/360',
        [Validators.required]
      ),
      bidRates: bids,
      askRates: asks,
      saved: new FormControl({
        value: curve ? true : false,
        disabled: true
      })
    });

    if (this.dateMode === 'TENOR') {
      form.addControl('tenors', tenors);
    } else {
      form.addControl('dates', dates);
    }

    if (curve) {
      Object.keys(curve).forEach(key => {
        if (!form.get(key)) {
          form.addControl(key, new FormControl(curve[key]));
        }
      });
    }

    return form;
  }

  public tenorControlMaker(tenor: string) {
    return new FormControl(tenor?.toLowerCase() || '', [Validators.required]);
  }

  public dateControlMaker(date: string) {
    return new FormControl(date || '', [Validators.required]);
  }

  public askBidControlMaker(value: any) {
    const control = new FormControl(
      !isNaN(value) && value !== ''
        ? this.percentPipe.transform(value, '1.0-12')
        : '',
      [Validators.required]
    );

    this.subSink.sink = control.valueChanges.subscribe(val => {
      Utilities.endOfTextBoxAdder(
        this.selectedPercentField,
        control,
        '%',
        !!control.value,
        'percentage',
        this.renderer
      );
    });

    return control;
  }

  public updateLocallyStoredData(
    treasuryCurves: TreasuryCurve[],
    entities?: Entity[]
  ) {
    if (entities) {
      // if we have passed in entities
      const identifiersInParentGroup = entities
        .filter(entity => isTrueLike(entity.isMemberOfParentGroup))
        .map(entity => entity.identifier);

      this.legService.updateEntities(entities, identifiersInParentGroup);
    }

    const deposityTakersAndCurrencies = this.identifiers.map(identifier => {
      return {
        entity: identifier,
        currencies: treasuryCurves
          .filter(curve => curve.entity === identifier)
          .map(curve => curve.currency)
      };
    });

    this.tradesCaptureService.setDepositTakersAndCurrencies(
      deposityTakersAndCurrencies
    );
  }

  public textBoxCaretRestrictionHandler(event, action: string) {
    // used to get the index of the item separating editable parts of text and non editable parts
    const indexLocator = Utilities.getIndexOfSeparator(event.target.value, '%');
    if (action === 'keydown') {
      Utilities.textBoxKeydownRestrictor(event, indexLocator, this.renderer);
    } else {
      Utilities.textBoxClickRestrictor(event, indexLocator, this.renderer);
    }
  }

  public deleteDateOrTenor(dateMode: DateMode) {
    (this.tenorAskBidArray('askRates') as FormArray).removeAt(
      this.selectedTenorIndex
    );
    (this.tenorAskBidArray('askRates') as FormArray).markAsDirty();

    (this.tenorAskBidArray('bidRates') as FormArray).removeAt(
      this.selectedTenorIndex
    );
    (this.tenorAskBidArray('bidRates') as FormArray).markAsDirty();

    if (dateMode === 'TENOR') {
      const tenors = this.tenorAskBidArray('tenors') as FormArray;
      tenors.removeAt(this.selectedTenorIndex);
      tenors.markAsDirty();
    } else {
      const dates = this.tenorAskBidArray('dates') as FormArray;
      dates.removeAt(this.selectedTenorIndex);
      dates.markAsDirty();
    }
    this.selectedTenorIndex = null;
  }

  public deleteCurve() {
    const curveToDelete = this.curvesArray.controls[
      this.selectedCurveIndex
    ] as FormGroup;

    if (curveToDelete.get('saved').value) {
      this.curvesToDelete.push(curveToDelete.value);
    }

    this.curvesArray.removeAt(this.selectedCurveIndex);
    this.selectedCurveIndex = null;
  }

  public get curvesArray() {
    return this.treasuryCurvesForm.get('curves') as FormArray;
  }

  public tenorAskBidArray(arrayName: string): FormArray {
    if (this.selectedCurveIndex !== null) {
      // if there is curve selected, bet the spreads and tenors array of that curve
      return this.treasuryCurvesFormCurves.controls[
        this.selectedCurveIndex
      ].get(arrayName) as FormArray;
    }
    return new FormArray([]);
  }

  public addNewCurve() {
    this.treasuryCurvesFormCurves.push(this.createTreasuryCurveForm(null));

    this.updateScroll('curves');
  }

  public addNewDateOrTenor(dateMode: DateMode) {
    if (this.selectedCurveIndex !== null) {
      const fg = this.treasuryCurvesFormCurves.controls[
        this.selectedCurveIndex
      ] as FormGroup;
      if (dateMode === 'TENOR') {
        (fg.get('tenors') as FormArray).push(this.tenorControlMaker(null));
      } else {
        (fg.get('dates') as FormArray).push(this.dateControlMaker(null));
      }

      (fg.get('bidRates') as FormArray).push(this.askBidControlMaker(''));
      (fg.get('askRates') as FormArray).push(this.askBidControlMaker(''));
    }

    this.updateScroll('tenors');
  }

  public saveChanges() {
    const changedRows = (
      this.treasuryCurvesForm.get('curves') as FormArray
    ).controls.filter(entity => entity.dirty === true); // get all the rows that have been altered

    let changes = {};

    const processRow = (
      rowValue: Record<string, any[]>,
      index: number,
      mode: 'write' | 'delete'
    ) => {
      rowValue.askRates = rowValue.askRates.map(ask => {
        return Utilities.percentageToNumber(ask); // turn 6% into 0.06
      });

      rowValue.bidRates = rowValue.bidRates.map(bid => {
        return Utilities.percentageToNumber(bid); // turn 6% into 0.06
      });

      changes[`${mode}${index}`] = this.tradesCaptureService.getUserInfoRequest(
        {
          task: `treasuryCurve:${mode}`,
          item: rowValue
        }
      );
    };

    changedRows
      .map((row: FormGroup) => row.value as Record<string, any[]>)
      .forEach((rowValue, index) => {
        processRow(rowValue, index, 'write');
      });

    this.curvesToDelete.forEach((row: Record<string, any[]>, index: number) => {
      processRow(row, index, 'delete');
    });

    this.savingCurvesStatus = 'LOADING';

    forkJoin(changes).subscribe({
      next: () => {
        changedRows.forEach((row: FormControl) => {
          row.get('saved').setValue(true);
        });
        this.treasuryCurvesForm.markAsPristine();
        this.curvesToDelete = [];
        this.savingCurvesStatus = 'DONE';
        this.updateLocallyStoredData(
          this.treasuryCurvesForm.get('curves').value
        );
        this.changeRef.markForCheck();
        this.resetSavingStatus();
      },
      error: () => {
        this.savingCurvesStatus = 'ERROR';
        this.changeRef.markForCheck();
        this.resetSavingStatus();
      }
    });
  }

  public resetSavingStatus() {
    setTimeout(() => {
      this.savingCurvesStatus = '';
      this.changeRef.markForCheck();
    }, 1500);
  }

  public updateScroll(table: string) {
    setTimeout(() => {
      let e =
        table === 'curves'
          ? this.divToScrollCurves?.nativeElement
          : this.divToScrollTenor?.nativeElement;
      if (e) {
        e.scrollTop = e.scrollHeight;
      }
    }, 0);
  }

  public get allowedToSave(): boolean {
    return (
      this.savingCurvesStatus !== 'LOADING' &&
      this.treasuryCurvesForm.valid &&
      ((!this.treasuryCurvesForm.pristine &&
        this.treasuryCurvesFormCurves.length > 0) ||
        this.curvesToDelete.length > 0)
    );
  }

  public get showTreasuryCurves() {
    return this.tradesCaptureService.showTreasuryCurves;
  }

  public percentRateValidator(control: AbstractControl): KeyValue | null {
    if (new RegExp('^\\d+(\\.\\d+)?%$').test(control.value)) {
      const valueOnly = control.value.substring(0, control.value.length - 1);
      if (!isNaN(valueOnly) && valueOnly >= 0 && valueOnly < 100) {
        return null;
      }
    }
    return { invalidEntry: { value: control.value } };
  }

  public spreadValidator(control: AbstractControl): KeyValue | null {
    if (new RegExp('^\\d+(\\.\\d+)?%$').test(control.value)) {
      const valueOnly = control.value.substring(0, control.value.length - 1);
      if (!isNaN(valueOnly) && valueOnly >= 0 && valueOnly <= 100) {
        return null;
      }
    }
    return { invalidEntry: { value: control.value } };
  }

  public modalChanged(event: boolean) {
    this.tradesCaptureService.setShowTreasuryCurves = event;
  }
}
